import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * 10 prioriteitstalen (NL / EN / DE / FR / ES / IT / PL / PT / SV / DA) zijn LIVE — bestuurd door i18next.
 * De overige 15 EU-talen staan in de dropdown als "Binnenkort" en zijn
 * uitgeschakeld tot er menselijk-geverifieerde vertalingen beschikbaar zijn.
 */
const LANGUAGES = [
  { code: "nl", flag: "🇳🇱", label: "Nederlands",  active: true },
  { code: "en", flag: "🇬🇧", label: "English",     active: true },
  { code: "de", flag: "🇩🇪", label: "Deutsch",     active: true },
  { code: "fr", flag: "🇫🇷", label: "Français",    active: true },
  { code: "es", flag: "🇪🇸", label: "Español",     active: true },
  { code: "it", flag: "🇮🇹", label: "Italiano",    active: true },
  { code: "pt", flag: "🇵🇹", label: "Português",   active: true },
  { code: "pl", flag: "🇵🇱", label: "Polski",      active: true },
  { code: "sv", flag: "🇸🇪", label: "Svenska",     active: true },
  { code: "da", flag: "🇩🇰", label: "Dansk",       active: true },
  { code: "fi", flag: "🇫🇮", label: "Suomi",       active: false },
  { code: "no", flag: "🇳🇴", label: "Norsk",       active: false },
  { code: "cs", flag: "🇨🇿", label: "Čeština",     active: false },
  { code: "sk", flag: "🇸🇰", label: "Slovenčina",  active: false },
  { code: "hu", flag: "🇭🇺", label: "Magyar",      active: false },
  { code: "ro", flag: "🇷🇴", label: "Română",      active: false },
  { code: "bg", flag: "🇧🇬", label: "Български",   active: false },
  { code: "el", flag: "🇬🇷", label: "Ελληνικά",    active: false },
  { code: "hr", flag: "🇭🇷", label: "Hrvatski",    active: false },
  { code: "sl", flag: "🇸🇮", label: "Slovenščina", active: false },
  { code: "et", flag: "🇪🇪", label: "Eesti",       active: false },
  { code: "lv", flag: "🇱🇻", label: "Latviešu",    active: false },
  { code: "lt", flag: "🇱🇹", label: "Lietuvių",    active: false },
  { code: "mt", flag: "🇲🇹", label: "Malti",       active: false },
  { code: "ga", flag: "🇮🇪", label: "Gaeilge",     active: false },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentCode = (i18n.language || "nl").split("-")[0];
  const active = LANGUAGES.find((l) => l.code === currentCode) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const pick = (code) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
        className="flex h-10 items-center gap-1.5 rounded-md bg-white/10 px-2.5 text-white transition hover:bg-white/20"
        title="Taal wisselen"
      >
        <span className="text-lg leading-none" aria-hidden>{active.flag}</span>
        <span className="hidden font-mono text-xs font-bold uppercase tracking-wider sm:inline">
          {active.code}
        </span>
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Selecteer taal"
          data-testid="language-switcher-menu"
          className="absolute right-0 top-full z-50 mt-2 max-h-[420px] w-64 overflow-y-auto rounded-md border border-zinc-200 bg-white shadow-2xl shadow-zinc-900/20"
        >
          <div className="sticky top-0 border-b border-zinc-100 bg-white px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            EU & internationaal · 25 talen
          </div>
          <ul className="py-1">
            {LANGUAGES.map((lang) => {
              const isCurrent = lang.code === currentCode;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isCurrent}
                    disabled={!lang.active}
                    onClick={() => lang.active && pick(lang.code)}
                    data-testid={`language-option-${lang.code}`}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition ${
                      isCurrent
                        ? "bg-nibro-sage-pale font-bold text-zinc-900"
                        : lang.active
                        ? "text-zinc-800 hover:bg-zinc-50"
                        : "text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
                    }`}
                  >
                    <span className="flex-shrink-0 text-lg leading-none" aria-hidden>
                      {lang.flag}
                    </span>
                    <span className="flex-1 truncate">{lang.label}</span>
                    {isCurrent ? (
                      <Check size={14} className="flex-shrink-0 text-nibro-sage-dark" aria-label="Actief" />
                    ) : lang.active ? (
                      <span className="flex-shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-800">
                        Beta
                      </span>
                    ) : (
                      <span className="flex-shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-800">
                        Binnenkort
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-zinc-100 bg-zinc-50 px-3 py-2 font-mono text-[10px] leading-snug text-zinc-500">
            Beta-vertalingen worden door ARAMFIX gevalideerd. Vragen?
            <a href="mailto:info@aramfix.com" className="ml-1 font-bold text-nibro-sage-dark hover:underline">
              info@aramfix.com
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
